import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, BarChart3, Calendar, Car, Layers, Plus, Search, TrendingUp, Upload } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import PageHeader from '../components/workspace/PageHeader';
import MetricStrip from '../components/workspace/MetricStrip';
import WorkspacePanel from '../components/workspace/WorkspacePanel';
import StatusChip from '../components/workspace/StatusChip';
import DataTableShell from '../components/workspace/DataTableShell';
import ProgramEditDrawer from '../components/edit/ProgramEditDrawer';
import programsApi from '../api/programs';

const formatMoney = (value) => {
  const number = Number(value || 0);
  if (Math.abs(number) >= 1000000) return `$${(number / 1000000).toFixed(1)}M`;
  if (Math.abs(number) >= 1000) return `$${(number / 1000).toFixed(0)}K`;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
  }).format(number);
};

const formatNumber = (value) => new Intl.NumberFormat('en-US').format(Number(value || 0));

const formatDate = (value) => {
  if (!value) return 'Not set';
  return new Date(value).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
};

const statusTone = (status) => {
  const normalized = (status || '').toLowerCase();
  if (['active', 'awarded', 'production', 'sourced'].includes(normalized)) return 'green';
  if (['planning', 'rfq', 'quoted', 'target'].includes(normalized)) return 'blue';
  if (['lost', 'inactive'].includes(normalized)) return 'slate';
  return 'amber';
};

export default function ProgramsIndex() {
  const navigate = useNavigate();
  const [programs, setPrograms] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPrograms = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await programsApi.getPrograms();
      setPrograms(data?.programs || []);
    } catch (err) {
      setError(err.message || 'Unable to load programs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPrograms();
  }, []);

  const filteredPrograms = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return programs;

    return programs.filter((program) => [
      program.name,
      program.code,
      program.oem,
      program.platform,
      program.status
    ].some((value) => String(value || '').toLowerCase().includes(query)));
  }, [programs, searchQuery]);

  const metrics = useMemo(() => {
    const activePrograms = programs.filter((program) => !['lost', 'inactive'].includes((program.status || '').toLowerCase()));
    const linkedParts = programs.reduce((sum, program) => sum + Number(program.part_count || 0), 0);
    const annualVolume = programs.reduce((sum, program) => sum + Number(program.total_annual_volume || 0), 0);
    const annualValue = programs.reduce((sum, program) => sum + Number(program.total_annual_value || 0), 0);

    return [
      {
        label: 'Active Programs',
        value: activePrograms.length,
        source: `${programs.length} total records`,
        icon: Car,
        tone: 'blue'
      },
      {
        label: 'Linked Parts',
        value: linkedParts,
        source: 'part-to-program links',
        icon: Layers,
        tone: 'green'
      },
      {
        label: 'Annual Volume',
        value: formatNumber(annualVolume),
        source: 'planning basis',
        icon: BarChart3,
        tone: 'amber'
      },
      {
        label: 'Annual Value',
        value: formatMoney(annualValue),
        source: 'current program rollup',
        icon: TrendingUp,
        tone: 'violet'
      }
    ];
  }, [programs]);

  return (
    <div className="space-y-5">
      <PageHeader
        eyebrow="Programs"
        title="Program 360"
        description="Manage vehicle programs, linked parts, market forecast basis, S&OP planning volume, and commercial value."
        meta="Open a program to link parts, market forecasts, S&OP rows, prices, and claims."
        actions={(
          <>
            <Button variant="outline" onClick={() => navigate('/import')}>
              <Upload className="h-4 w-4" />
              Import Programs / Parts
            </Button>
            <Button variant="outline" onClick={() => navigate('/pipeline')}>
              Acquisition Pipeline
              <ArrowRight className="h-4 w-4" />
            </Button>
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4" />
              New Program
            </Button>
          </>
        )}
      />

      <ProgramEditDrawer
        open={createOpen}
        onOpenChange={setCreateOpen}
        program={null}
        onSaved={async (programId) => {
          await fetchPrograms();
          if (programId) navigate(`/programs/${programId}`);
        }}
      />

      <MetricStrip metrics={metrics} />

      <WorkspacePanel
        title="Program Portfolio"
        subtitle="Open a row to inspect the connected parts, market forecast basis, S&OP view, claims, and approval context."
        actions={(
          <div className="relative w-72">
            <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
            <Input
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Search programs, OEMs, platforms..."
              className="pl-9"
            />
          </div>
        )}
      >
        {error ? (
          <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div>
        ) : (
          <DataTableShell>
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50">
                <tr>
                  {['Program', 'OEM', 'Platform', 'Status', 'SOP / EOP', 'Parts', 'Annual Volume', 'Annual Value', ''].map((column) => (
                    <th key={column} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                      {column}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {loading ? (
                  <tr>
                    <td colSpan={9} className="px-4 py-8 text-center text-slate-500">Loading programs...</td>
                  </tr>
                ) : filteredPrograms.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-4 py-8">
                      <div className="mx-auto max-w-xl text-center">
                        <div className="text-sm font-medium text-slate-950">No programs found for this workspace.</div>
                        <p className="mt-1 text-xs text-slate-500">
                          Create a program manually for one-off setup, or import programs, parts, forecasts, and prices for customer-scale onboarding.
                        </p>
                        <div className="mt-4 flex flex-wrap justify-center gap-2">
                          <Button size="sm" onClick={() => setCreateOpen(true)}>
                            <Plus className="h-3.5 w-3.5" />
                            New Program
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => navigate('/import')}>
                            <Upload className="h-3.5 w-3.5" />
                            Import Programs / Parts
                          </Button>
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : filteredPrograms.map((program) => (
                  <tr
                    key={program.id}
                    className="cursor-pointer hover:bg-blue-50/60"
                    onClick={() => navigate(`/programs/${program.id}`)}
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-950">{program.name || 'Untitled program'}</div>
                      <div className="text-xs text-slate-500">{program.code || 'No program code'}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-700">{program.oem || 'Not set'}</td>
                    <td className="px-4 py-3 text-slate-700">{program.platform || 'Not set'}</td>
                    <td className="px-4 py-3">
                      <StatusChip tone={statusTone(program.status)}>{program.status || 'Planning'}</StatusChip>
                    </td>
                    <td className="px-4 py-3 text-slate-700">
                      <div className="flex items-center gap-1.5 whitespace-nowrap">
                        <Calendar className="h-3.5 w-3.5 text-slate-400" />
                        <span>{formatDate(program.start_date)} / {formatDate(program.end_date)}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-700">{program.part_count || 0}</td>
                    <td className="px-4 py-3 text-slate-700">{formatNumber(program.total_annual_volume)}</td>
                    <td className="px-4 py-3 font-medium text-slate-900">{formatMoney(program.total_annual_value)}</td>
                    <td className="px-4 py-3 text-right">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(event) => {
                          event.stopPropagation();
                          navigate(`/programs/${program.id}`);
                        }}
                      >
                        Program 360
                        <ArrowRight className="ml-2 h-3.5 w-3.5" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </DataTableShell>
        )}
      </WorkspacePanel>
    </div>
  );
}
